/**
 * The crash-loop breaker.
 *
 * The app remembers where you were looking, which is right nearly always and
 * catastrophic in one case: if the view itself is what kills the app, restoring
 * it means the app dies on every launch, for ever. Edd hit exactly that panning
 * across the Atlantic — the app died over American water, reopened over
 * American water, and died again, and the only way out was clearing app data
 * and losing the queued bug reports with it. An app that cannot recover from
 * its own saved state is one bad view away from bricked.
 *
 * So: count the launches that die young. Two deaths inside a minute and the
 * next start ignores the saved view and comes back at the home position. Once a
 * launch has survived long enough to be called healthy the count resets, so
 * ordinary use never trips it and the mechanism costs nothing.
 *
 * Deliberately independent of the heartbeat's verdict. The heartbeat explains
 * what happened AFTER the fact and needs the app to have run long enough to
 * write pulses; this has to work when the app dies before it can say anything,
 * which is the case that traps you. Two counters, two jobs.
 *
 * Storage failures are swallowed: a device with no localStorage should start
 * normally rather than not at all.
 */

const KEY = 'acp.boot.attempts.v1';

/** Two deaths in a row before we distrust the saved view. */
const TRIP_AT = 2;

/**
 * How recent the last attempt must be to count as the same loop. A launch a
 * week later is not part of this morning's trouble.
 */
const LOOP_WINDOW_MS = 60_000;

/**
 * How long a launch must survive to be called healthy. Long enough to be past
 * the chart load, which is where a bad view does its damage, and short enough
 * that a skipper who opens the app for ten seconds still clears the count.
 */
export const HEALTHY_AFTER_MS = 25_000;

interface Attempts {
  /** Consecutive launches that never reached the healthy mark. */
  count: number;
  /** When the last attempt started, wall clock ms. */
  at: number;
}

function read(): Attempts {
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    if (!raw) return { count: 0, at: 0 };
    const v = JSON.parse(raw) as Partial<Attempts>;
    return {
      count: typeof v.count === 'number' && Number.isFinite(v.count) ? v.count : 0,
      at: typeof v.at === 'number' && Number.isFinite(v.at) ? v.at : 0,
    };
  } catch {
    return { count: 0, at: 0 };
  }
}

function write(a: Attempts): void {
  try {
    globalThis.localStorage?.setItem(KEY, JSON.stringify(a));
  } catch {
    /* a device that cannot remember this still starts */
  }
}

/**
 * Called once, as early as possible. Records this attempt and says whether the
 * app should distrust its saved view.
 *
 * Pure but for the counter, and the counter is the point: it is written BEFORE
 * the dangerous work, so a launch that dies during that work leaves its mark.
 */
export function noteBootAttempt(nowMs: number = Date.now()): boolean {
  const prev = read();
  // An attempt long ago is not part of this loop, so the count starts again.
  const withinLoop = prev.at !== 0 && nowMs - prev.at < LOOP_WINDOW_MS;
  const count = withinLoop ? prev.count + 1 : 1;
  write({ count, at: nowMs });
  rescuing = count > TRIP_AT;
  return rescuing;
}

/**
 * Called once the app has been alive long enough to be trusted. Clears the
 * count so the next launch restores the saved view as usual.
 */
export function markBootHealthy(): void {
  write({ count: 0, at: 0 });
}

/**
 * Whether THIS launch is a rescue launch, for code that runs after the map has
 * already decided. noteBootAttempt is called once, early, by MapView; anything
 * else that wants to stand down on a rescue asks this.
 */
let rescuing = false;

/** Set by noteBootAttempt; read by anything that should do less on a rescue. */
export function isRescueStart(): boolean {
  return rescuing;
}

/** Test seam: forget everything this module remembers. */
export function __resetBootAttempts(): void {
  rescuing = false;
  try {
    globalThis.localStorage?.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
